import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, FileText, Braces, Quote, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { ArticleCard, AgentPanel } from "./KnowledgePage";
import { NoteCard } from "./FieldNotes";

const API = process.env.REACT_APP_BACKEND_URL;
const PUB = typeof window !== "undefined" && window.location.hostname.endsWith("margtor.africa") ? "https://margtor.africa" : API;

export const ArticlePage = ({ kind = "article" }) => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const k = t.knowledge;
  const isNote = kind === "note";
  const apiPath = `/api/knowledge/${isNote ? "notes/" : ""}${slug}`;
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);
  const [cited, setCited] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setA(null);
    setMissing(false);
    fetch(`${API}${apiPath}?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setA)
      .catch(() => setMissing(true));
  }, [apiPath, lang]);

  const cite = async () => {
    const text = `${isNote ? "Gregory Radek. " : ""}${a.title}. MargTor Ltd., ${a.updated}. ${a.url} (CC BY 4.0)`;
    try {
      await navigator.clipboard.writeText(text);
      setCited(true);
      toast.success(k.citeCopied);
      setTimeout(() => setCited(false), 2500);
    } catch {
      toast.error(k.citeFailed);
    }
  };

  if (missing) {
    return (
      <main data-testid="article-missing" className="bg-canvas pt-[68px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-32 text-center">
          <h1 className="font-display text-3xl font-black text-ink">{k.notFound}</h1>
          <Link to="/knowledge" className="mt-6 inline-block font-mono-tech text-xs uppercase tracking-[0.2em] text-red-brand">{k.backToHub}</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="article-page">
      <section className="relative bg-canvas bp-grid pt-[68px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 pt-12 sm:pt-20 pb-12">
          <Link to={`/knowledge${lang === "en" ? "" : `?lang=${lang}`}${isNote ? "#notes" : ""}`} data-testid="article-back" className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#525964] hover:text-red-brand transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> {isNote ? k.notes.back : k.backToHub}
          </Link>
          {a ? (
            <div className="mt-8 max-w-4xl">
              <Reveal>
                <div className="flex flex-wrap items-center gap-3 font-mono-tech text-[10.5px] uppercase tracking-[0.2em] text-[#525964]">
                  <span className="text-red-brand">{isNote ? k.notes.kind : k.eyebrow}</span>
                  <span>·</span>
                  <span>{a.reading_minutes} {k.minRead}</span>
                  <span>·</span>
                  <time dateTime={a.updated}>{k.updated} {a.updated}</time>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 data-testid="article-title" className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-ink">{a.title}</h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p data-testid="article-summary" className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-[#525964]">{a.summary}</p>
              </Reveal>
              {isNote && (
                <Reveal delay={0.22}>
                  <div className="mt-6 flex items-center gap-3" data-testid="note-author">
                    <img src="/assets/img/founder-gregory-studio.jpg" alt="Gregory Radek" width={44} height={44} className="h-11 w-11 object-cover grayscale" />
                    <div>
                      <p className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-ink">{k.notes.byline}</p>
                      <a href="https://www.linkedin.com/in/gregradek/" target="_blank" rel="noopener noreferrer me" className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-red-brand hover:text-ink transition-colors">{k.notes.linkedin}</a>
                    </div>
                  </div>
                </Reveal>
              )}
            </div>
          ) : (
            <div className="mt-8 h-40 max-w-3xl animate-pulse bg-white/60" />
          )}
        </div>
      </section>

      <section className="relative bg-sand border-y border-technical py-16 sm:py-24">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <article className="lg:col-span-8">
            {a && (
              <div className="kb-article" data-testid="article-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.body}</ReactMarkdown>
              </div>
            )}
          </article>
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 self-start">
            {a && (
              <div className="border border-technical bg-white p-6" data-testid="article-tools">
                <p className="font-mono-tech text-[0.65rem] tracking-[0.28em] uppercase text-red-brand">{k.toolsEyebrow}</p>
                <div className="mt-4 flex flex-col gap-2">
                  <button type="button" onClick={cite} data-testid="article-cite" className="chamfer-sm inline-flex items-center gap-2 bg-ink px-4 py-2.5 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] hover:bg-red-brand transition-colors">
                    {cited ? <Check className="h-4 w-4" /> : <Quote className="h-4 w-4" />} {cited ? k.citeCopied : k.cite}
                  </button>
                  <a href={`${PUB}${apiPath}.md?lang=${lang}`} target="_blank" rel="noopener noreferrer" data-testid="article-markdown" className="inline-flex items-center gap-2 border border-technical px-4 py-2.5 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] text-ink hover:border-red-brand transition-colors">
                    <FileText className="h-4 w-4 text-red-brand" /> {k.openMarkdown}
                  </a>
                  <a href={`${PUB}${apiPath}?lang=${lang}`} target="_blank" rel="noopener noreferrer" data-testid="article-json" className="inline-flex items-center gap-2 border border-technical px-4 py-2.5 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] text-ink hover:border-red-brand transition-colors">
                    <Braces className="h-4 w-4 text-red-brand" /> {k.openJson}
                  </a>
                </div>
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {a.tags.map((tag) => (
                    <span key={tag} className="border border-technical px-2 py-0.5 font-mono-tech text-[9.5px] uppercase tracking-[0.15em] text-[#525964]">{tag}</span>
                  ))}
                </div>
                <p className="mt-5 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#525964]">
                  {k.license.label}: <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer license" className="text-ink underline decoration-red-brand/60 underline-offset-4">CC BY 4.0</a>
                </p>
              </div>
            )}
            <AgentPanel k={k} compact lang={lang} />
          </aside>
        </div>
      </section>

      {a && (
        <section className="relative bg-canvas bp-grid py-16 sm:py-24">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
            <p className="eyebrow" data-testid="article-related-eyebrow">{k.related}</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {a.related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.07}>
                  {r.kind === "note" ? (
                    <div className="h-full bg-ink"><NoteCard n={r} lang={lang} k={k} testid={`related-card-${r.slug}`} /></div>
                  ) : (
                    <ArticleCard a={r} lang={lang} k={k} testid={`related-card-${r.slug}`} />
                  )}
                </Reveal>
              ))}
            </div>
            <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5 border border-technical bg-white p-6 sm:p-8">
              <div className="flex-1">
                <p className="font-display text-xl font-bold tracking-tight text-ink">{k.ctaTitle}</p>
                <p className="mt-2 text-sm text-[#525964]">{k.ctaDesc}</p>
              </div>
              <Link to="/partners" data-testid="article-cta-partners" className="chamfer-sm bg-red-brand px-5 py-3 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] hover:bg-[#5f1019] transition-colors">
                {t.nav.cta}
              </Link>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};
